<script>
  import { UserModal, InfoBox } from "../../components/index"
  import { Button, Icon, Text, Tag, Badge, Flex, FlexItem } from "@gl/design-system"
  import { faPen, faInfoCircle } from "@fortawesome/pro-regular-svg-icons"
  import { faInfoCircle as faInfoCircleSolid } from "@fortawesome/pro-solid-svg-icons"
  import { _ } from "../../stores/index"
  import { format } from "date-fns"
  import { createEventDispatcher, getContext, onMount } from "svelte"
  const dispatch = createEventDispatcher()
  const { isAuthorized } = getContext("isAuthorized")

  export let user

  let modal
  let modalType

  let statusInfo
  let connectionInfo

  let statusMap = {
    blocked: "error",
    unlinked: "error",
    null: "warning",
    active: "success",
  }

  function defineConnectionType() {
    if (!user.data.externalId) {
      user.connection = "pending"
      return null
    }

    const splitExternalID = user.data.externalId.split("|")
    if (splitExternalID.length === 1) user.connection = "manual"
    if (splitExternalID.length === 2) user.connection = splitExternalID[0]
    if (splitExternalID.length > 2) user.connection = splitExternalID[1]
  }

  onMount(() => {
    defineConnectionType()
  })
</script>

{#if modal}
  <UserModal
    bind:active={modal}
    on:close={() => {
      modal = false
    }}
    on:success={(e) => dispatch("change", { userId: e.detail.userId })}
    data={{ id: user.id, ...user.data }}
    {modalType}
  />
{/if}

{#if $isAuthorized("user_write")}
  <Button
    class="mt-3 mb-4"
    outlined
    size="small"
    on:click={() => {
      modal = true
      modalType = "put"
    }}
  >
    <Icon icon={faPen} />
    <Text>{$_("buttons.edit")}</Text>
  </Button>
{/if}

<div class="w-100 user-main-card">
  <div class="user-row" id="user-name">
    <div class="user-title">
      {$_("common.name")}
    </div>
    <div class="user-td">
      {`${user.data.firstName} ${user.data.lastName}`}
    </div>
  </div>

  <div class="user-row" id="user-email">
    <div class="user-title">
      {$_("users.email_address")}
    </div>
    <div class="user-td">
      {user.data.email}
    </div>
  </div>

  <div class="user-row" id="user-groups">
    <div class="user-title">
      {$_("common.groups")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.groups?.length > 0}
        {#each user.groups as group}
          <p class="mb-2" style="">{group && group.name}</p>
        {/each}
      {:else}
        -
      {/if}
    </div>
  </div>
  <div class="user-row" id="user-roles">
    <div class="user-title">
      {$_("common.roles")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.roles?.length > 0}
        {#each user.roles as role}
          <p class="mb-2" style="">{role && role.name}</p>
        {/each}
      {/if}
      {#if user.data.isMaster}
        <Badge class={user.roles > 0 && "mt-2"} status="success" label={$_("users.master")} />
      {:else}
        -
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-apps">
    <div class="user-title">
      {$_("common.apps")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.apps?.length > 0}
        {#each user.apps as app}
          {#if app}
            <p class="mb-2" style="">
              {app.name}
              <span class="text-duisab">({app.type})</span>
            </p>
          {/if}
        {/each}
      {:else}
        -
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-status" role="status">
    <div class="user-title">
      {$_("common.status")}
    </div>
    <div class="user-td" style="display: block">
      <Flex class="align-items-center">
        <FlexItem>
          <Tag status={statusMap[user.data.status]}>
            {$_(`users.status.${user.data.status}`)}
          </Tag>
        </FlexItem>

        <FlexItem style="position:relative">
          <InfoBox title={$_("common.status")}>{$_(`users.status.${user.data.status}.tooltip`)}</InfoBox>
        </FlexItem>
      </Flex>
    </div>
  </div>

  <div class="user-row" id="user-connection" role="status">
    <div class="user-title">
      {$_("common.connection")}
    </div>
    <div class="user-td">
      <Flex class="align-items-center">
        <FlexItem>
          <Badge
            status={user.connection === "pending" && "error"}
            label={$_(`users.connection.${user.connection}`, { default: $_("users.connection.manual") })}
          />
        </FlexItem>

        <FlexItem style="position:relative">
          <InfoBox title={$_("common.connection")}>
            {$_(`users.connection.${user.connection}.tooltip`, {
              default: $_("users.connection.manual.tooltip"),
            })}
          </InfoBox>
        </FlexItem>
      </Flex>
    </div>
  </div>

  <div class="user-row" id="user-created">
    <div class="user-title">
      {$_("common.created")}
    </div>
    <div class="user-td">
      {user.meta?.createdAt ? format(new Date(user.meta.createdAt), "dd.MM.yyyy HH:mm:ss") : "-"}
      {#if user.meta?.createdAt}
        <span class="text-light ml-2" style="font-size:14px">{$_("common.by")} {user.meta.createdBy}</span>
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-modified">
    <div class="user-title">
      {$_("common.last.modified")}
    </div>
    <div class="user-td">
      {user.meta?.modifiedAt ? format(new Date(user.meta.modifiedAt), "dd.MM.yyyy HH:mm:ss") : "-"}
      {#if user.meta?.modifiedBy}
        <span class="text-light ml-2" style="font-size:14px">{$_("common.by")} {user.meta.modifiedBy}</span>
      {/if}
    </div>
  </div>
</div>
